'use client'

import { createContext, useContext, type ReactNode } from 'react'

import { Info } from 'lucide-react'

import { cn } from '@/shared/lib/utils'
import {
  useFormField,
  FormField,
  FormItem as ShadFormItem,
  FormLabel as ShadFormLabel,
  FormControl as ShadFormControl,
  FormDescription as ShadFormDescription,
  FormMessage as ShadFormMessage
} from '@/shared/ui/form'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shared/ui/tooltip'

// Re-export for use in other form components
export { useFormField }

type FormItemContextValue = {
  field: { name: string; defaultValue?: unknown; disabled?: boolean }
}
const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue)
export const useFormItem = () => useContext(FormItemContext)

type FormItemProps = {
  name: string
  defaultValue?: unknown
  disabled?: boolean
  children: ReactNode
  className?: string
  vertical?: boolean
  horizontal?: boolean
  disableMessage?: boolean
}

function FormItem({
  name,
  defaultValue,
  disabled,
  children,
  className = '',
  vertical = true,
  horizontal = false,
  disableMessage = false
}: FormItemProps) {
  const isHorizontal = horizontal && !vertical
  return (
    <FormField
      name={name}
      defaultValue={defaultValue}
      disabled={disabled}
      render={field => (
        <FormItemContext.Provider value={field}>
          <ShadFormItem
            className={cn(
              'group flex flex-col gap-2',
              { 'md:grid md:grid-cols-2 md:gap-x-4': isHorizontal },
              className
            )}
            data-orientation={isHorizontal ? 'horizontal' : 'vertical'}
          >
            {children}
            {!disableMessage && (
              <ShadFormMessage className="group-data-[orientation=horizontal]:col-start-2" />
            )}
          </ShadFormItem>
        </FormItemContext.Provider>
      )}
    />
  )
}

type FormLabelProps = {
  children: ReactNode
  required?: boolean
  labelInfo?: string
  className?: string
}

function FormLabel({ children, required, labelInfo, className }: FormLabelProps) {
  return (
    <ShadFormLabel className={cn('block', className)}>
      {children}
      {required && <span className="text-destructive ms-0.5">*</span>}
      {labelInfo && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger type="button" className="ms-1 align-middle">
              <Info size={14} className="hover:text-primary" />
            </TooltipTrigger>
            <TooltipContent>{labelInfo}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </ShadFormLabel>
  )
}

function FormControl({ children }: { children: ReactNode }) {
  return (
    <ShadFormControl className="group-data-[orientation=horizontal]:col-start-2 group-data-[orientation=horizontal]:items-start">
      {children}
    </ShadFormControl>
  )
}

const FormDescription = ({ children }: { children: ReactNode }) => (
  <ShadFormDescription className="group-data-[orientation=horizontal]:col-start-2">
    {children}
  </ShadFormDescription>
)

FormItem.Label = FormLabel
FormItem.Control = FormControl
FormItem.Description = FormDescription

export { FormItem }
